import { useMemo } from 'react';
import { useIntl, getCurrentLocale } from '@leagueplatform/locales';
import { EXTERNAL_URLS, SENTRY_CONSTANTS } from 'common/constants/constants';
import type {
  AppLocales,
  AppStoreLinkProps,
  FooterLink,
  LocalesKey,
} from 'common/types/types';
import { useGetScanUserProfile } from 'hooks/use-get-scan-user-profile/use-get-scan-user-profile.hook';
import { getAddresses } from 'common/utils/addresses';
import { captureAppError } from '@leagueplatform/observability';
import APP_STORE_ZH from 'assets/app-store/app-store-zh.png';
import APP_STORE_EN from 'assets/app-store/app-store-en.png';
import APP_STORE_ES from 'assets/app-store/app-store-es.png';
import APP_STORE_KO from 'assets/app-store/app-store-ko.png';
import APP_STORE_VI from 'assets/app-store/app-store-vi.png';
import GOOGLE_STORE_EN from 'assets/google-play-store/google-store-en.png';
import GOOGLE_STORE_ZH from 'assets/google-play-store/google-store-zh.png';
import GOOGLE_STORE_ES from 'assets/google-play-store/google-store-es.png';
import GOOGLE_STORE_KO from 'assets/google-play-store/google-store-ko.png';
import GOOGLE_STORE_VI from 'assets/google-play-store/google-store-vi.png';

interface StoreImages {
  googlePlayStoreImage: string;
  appStoreImage: string;
}

const localeStoreImages: Record<AppLocales, StoreImages> = {
  en: {
    googlePlayStoreImage: GOOGLE_STORE_EN,
    appStoreImage: APP_STORE_EN,
  },
  zh: {
    googlePlayStoreImage: GOOGLE_STORE_ZH,
    appStoreImage: APP_STORE_ZH,
  },
  es: {
    googlePlayStoreImage: GOOGLE_STORE_ES,
    appStoreImage: APP_STORE_ES,
  },
  ko: {
    googlePlayStoreImage: GOOGLE_STORE_KO,
    appStoreImage: APP_STORE_KO,
  },
  vi: {
    googlePlayStoreImage: GOOGLE_STORE_VI,
    appStoreImage: APP_STORE_VI,
  },
};

export const getBaseLocale = <T extends Record<AppLocales, StoreImages>>(
  localeObject: T,
): AppLocales => {
  const currentLocale = getCurrentLocale();
  const localeLanguage = currentLocale.split('-')[0] as AppLocales;

  return Object.hasOwn(localeObject, localeLanguage) ? localeLanguage : 'en';
};

const DEFAULT_STATE = 'CA';

const FOOTER_CONFIG_BY_STATE: Record<
  string,
  { descriptionId: LocalesKey; planName: string }
> = {
  CA: {
    descriptionId: 'STR_FOOTER_DESCRIPTION_CA',
    planName: 'SCAN Health Plan',
  },
  AZ: {
    descriptionId: 'STR_FOOTER_DESCRIPTION_AZ',
    planName: 'SCAN Desert Health Plan',
  },
  NM: {
    descriptionId: 'STR_FOOTER_DESCRIPTION_NM',
    planName: 'SCAN Health Plan New Mexico',
  },
  TX: {
    descriptionId: 'STR_FOOTER_DESCRIPTION',
    planName: 'SCAN Health Plan',
  },
  NV: {
    descriptionId: 'STR_FOOTER_DESCRIPTION',
    planName: 'SCAN Health Plan',
  },
};

const getFooterConfigForState = (state?: string) =>
  FOOTER_CONFIG_BY_STATE[state ?? DEFAULT_STATE] ??
  FOOTER_CONFIG_BY_STATE[DEFAULT_STATE];

export function useFooterConfig() {
  const { formatMessage } = useIntl();
  // Footer content is dynamic based on the user's residential state.
  // Supported states include: CA, AZ, TX, NV, and NM.
  const { data: userProfileData, isLoading } = useGetScanUserProfile();
  const userResidentialState = useMemo(() => {
    const addresses = getAddresses(userProfileData?.userProfile?.locations);
    const state = addresses?.oldResidentialAddress?.state;

    if (!state && !isLoading) {
      captureAppError(new Error('undefined footer address state'), {
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
        errorName: 'Undefined residential address state',
        context: {
          Context: {
            description: 'undefined footer address state',
          },
        },
      });
    }

    return state;
  }, [userProfileData, isLoading]);

  const baseLocale = getBaseLocale(localeStoreImages);

  const { googlePlayStoreImage, appStoreImage } = localeStoreImages[baseLocale];

  const footerLinks: FooterLink[] = [
    {
      to: EXTERNAL_URLS.NON_DISCRIMINATION_POLICY,
      target: '_blank',
      text: formatMessage({ id: 'STR_NON_DISCRIMINATION_POLICY' }),
    },
    {
      to: EXTERNAL_URLS.PRIVACY_POLICY,
      target: '_blank',
      text: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
    },
    {
      to: EXTERNAL_URLS.NOTICE_OF_PRIVACY_PRACTICES,
      target: '_blank',
      text: formatMessage({ id: 'STR_NOTICE_OF_PRIVACY_PRACTICES' }),
    },
    {
      to: EXTERNAL_URLS.TERMS_OF_USE,
      target: '_blank',
      text: formatMessage({ id: 'STR_TERMS_OF_USE' }),
    },
  ];

  const appLinks: AppStoreLinkProps[] = [
    {
      href: EXTERNAL_URLS.MOBILE_APPS.APP_STORE,
      alt: formatMessage({ id: 'STR_DOWNLOAD_APP_STORE' }),
      src: appStoreImage,
    },
    {
      href: EXTERNAL_URLS.MOBILE_APPS.GOOGLE_PLAY_STORE,
      alt: formatMessage({ id: 'STR_GET_GOOGLE_PLAY' }),
      src: googlePlayStoreImage,
    },
  ];

  const { descriptionId, planName } =
    getFooterConfigForState(userResidentialState);

  return {
    footerLinks,
    appLinks,
    footerDescriptionId: descriptionId,
    planName,
  };
}
